import React, { useState } from "react";
import { db } from "../firebase/firebaseConfig";
import {
  collection, doc, getDoc, setDoc, getDocs, writeBatch, query, limit as qLimit,
} from "firebase/firestore";

const COLL_LIVE = "cylinders_warehouse";
const COLL_DUP  = "cylinders_warehouse_duplicate";
const COLL_BACKUPS = "cylinders_warehouse_backups"; // backup doc {meta} + subcollection 'docs'
const ADMIN_LOCK = "admin/warehouse_lock";
const META_LATEST = "admin/warehouse_latest_backup";

const tsId = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const clearCollection = async (collPath, setStatus) => {
  const ref = collection(db, collPath);
  let total = 0;
  while (true) {
    const snap = await getDocs(query(ref, qLimit(500)));
    if (snap.empty) break;
    const batch = writeBatch(db);
    snap.docs.forEach((d) => batch.delete(doc(db, collPath, d.id)));
    await batch.commit();
    total += snap.size;
    setStatus?.(`Cleared ${total} from ${collPath}…`);
    await sleep(0);
  }
  return total;
};

const copyCollection = async (srcPath, dstPath, setStatus) => {
  const srcSnap = await getDocs(collection(db, srcPath));
  if (srcSnap.empty) return 0;

  let written = 0;
  let batch = writeBatch(db);
  let count = 0;

  for (const d of srcSnap.docs) {
    batch.set(doc(db, dstPath, d.id), d.data());
    count++; written++;
    if (count === 500) {
      await batch.commit();
      setStatus?.(`Copied ${written} from ${srcPath} → ${dstPath}…`);
      batch = writeBatch(db);
      count = 0;
      await sleep(0);
    }
  }
  if (count > 0) await batch.commit();
  return written;
};

const PublishDuplicateToLive = () => {
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  const lock = async (on) => {
    if (on) {
      await setDoc(doc(db, ADMIN_LOCK), {
        locked: true,
        reason: "Publishing duplicate to live",
        startedAt: new Date().toISOString(),
      }, { merge: true });
    } else {
      await setDoc(doc(db, ADMIN_LOCK), { locked: false, endedAt: new Date().toISOString() }, { merge: true });
    }
  };

  const publish = async () => {
    if (!window.confirm("This will BACKUP live, CLEAR live, then COPY duplicate to live. Continue?")) return;

    setBusy(true);
    try {
      setStatus("Locking warehouse…");
      await lock(true);

      // 1) BACKUP live → backups/{backupId}/docs
      const backupId = tsId();
      const backupRoot = doc(db, COLL_BACKUPS, backupId);
      const backupDocsPath = `${COLL_BACKUPS}/${backupId}/docs`;

      setStatus(`Creating backup ${backupId}…`);
      await setDoc(backupRoot, {
        backupId,
        createdAt: new Date().toISOString(),
        source: COLL_LIVE,
        count: 0,
      });

      setStatus("Copying LIVE → BACKUP…");
      const liveCount = await copyCollection(COLL_LIVE, backupDocsPath, setStatus);

      await setDoc(backupRoot, { count: liveCount }, { merge: true });
      await setDoc(doc(db, META_LATEST), { backupId, at: new Date().toISOString(), count: liveCount });

      // 2) CLEAR live
      setStatus("Clearing LIVE collection…");
      await clearCollection(COLL_LIVE, setStatus);

      // 3) COPY duplicate → live
      setStatus("Publishing DUPLICATE → LIVE…");
      const pubCount = await copyCollection(COLL_DUP, COLL_LIVE, setStatus);

      setStatus(`✅ Done. Live replaced with duplicate. Backed up ${liveCount}; published ${pubCount}.`);
    } catch (e) {
      console.error(e);
      setStatus("❌ Failed mid-publish. You can run Rollback to latest backup.");
    } finally {
      await lock(false);
      setBusy(false);
    }
  };

  const rollback = async () => {
    if (!window.confirm("Rollback LIVE to the latest backup? This will CLEAR LIVE and RESTORE from backup.")) return;

    setBusy(true);
    try {
      setStatus("Locking warehouse…");
      await lock(true);

      const latestSnap = await getDoc(doc(db, META_LATEST));
      if (!latestSnap.exists()) {
        setStatus("No latest backup pointer found.");
        return;
      }
      const { backupId } = latestSnap.data();
      const backupDocsPath = `${COLL_BACKUPS}/${backupId}/docs`;

      setStatus(`Clearing LIVE…`);
      await clearCollection(COLL_LIVE, setStatus);

      setStatus(`Restoring from backup ${backupId}…`);
      const restored = await copyCollection(backupDocsPath, COLL_LIVE, setStatus);

      setStatus(`✅ Rollback complete. Restored ${restored} docs from ${backupId}.`);
    } catch (e) {
      console.error(e);
      setStatus("❌ Rollback failed. Check console.");
    } finally {
      await lock(false);
      setBusy(false);
    }
  };

  return (
    <div className="p-4 max-w-xl mx-auto">
      <h2 className="text-xl font-semibold mb-2">Publish Duplicate → Live (with backup & rollback)</h2>
      <p className="text-sm text-gray-700 mb-4">
        1) Snapshot <code>{COLL_LIVE}</code> to <code>{COLL_BACKUPS}/&lt;id&gt;/docs</code> • 2) Clear live • 3) Copy from <code>{COLL_DUP}</code> to live.
        Uses a maintenance lock at <code>{ADMIN_LOCK}</code> and tracks latest backup at <code>{META_LATEST}</code>.
      </p>

      <div className="flex gap-2">
        <button
          onClick={publish}
          disabled={busy}
          className={`flex-1 py-3 rounded-md text-white ${busy ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-700"}`}
        >
          {busy ? "Working…" : "Publish duplicate to live"}
        </button>
        <button
          onClick={rollback}
          disabled={busy}
          className={`px-4 py-3 rounded-md text-white ${busy ? "bg-gray-400" : "bg-red-600 hover:bg-red-700"}`}
        >
          {busy ? "…" : "Rollback to latest"}
        </button>
      </div>

      {status && <p className="mt-3 text-sm text-gray-800">{status}</p>}

      <div className="mt-4 text-xs text-gray-600 space-y-1">
        <p>• This is **not atomic** across thousands of docs—hence the lock. Block allocations while status ≠ idle.</p>
        <p>• Backups are versioned (timestamp ID). To prune old backups, just delete the backup doc and its <code>/docs</code> subcollection.</p>
        <p>• Subcollections aren’t copied (you don’t use them on these docs anyway).</p>
      </div>
    </div>
  );
};

export default PublishDuplicateToLive;
